import { useEffect, useRef, useState, useCallback } from 'react';
import { ConnectionStatusEnum } from '../models/connection-status-enum';
import Config from '../config';

const RECONNECT_DELAY_MS = 5000;
// Server heartbeats every 25s. Reconnect if nothing arrives for 60s (~2.4× heartbeat),
// catching zombie TCP connections where the browser never fires onerror.
const STALE_THRESHOLD_MS = 60_000;
const WATCHDOG_INTERVAL_MS = 5_000;

export function useSSE(onReconnect: () => void) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionStatusEnum>(ConnectionStatusEnum.disconnected);
  const [connectionTransport, setConnectionTransport] = useState<string>('');
  const [connectionError, setConnectionError] = useState<string>('');
  const [replayCount, setReplayCount] = useState(0);
  const wasConnectedRef = useRef(false);
  const listenersRef = useRef<Array<{ type: string; handler: (e: MessageEvent) => void; wrapped: (e: MessageEvent) => void }>>([]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatHandlerRef = useRef<((e: MessageEvent) => void) | null>(null);
  const manualCloseRef = useRef(false);
  const lastEventAtRef = useRef<number>(Date.now());
  const lastEventIdRef = useRef<number>(0);

  const noteEvent = useCallback((event: MessageEvent) => {
    lastEventAtRef.current = Date.now();
    if (event.lastEventId) {
      const parsed = parseInt(event.lastEventId, 10);
      if (!Number.isNaN(parsed) && parsed > lastEventIdRef.current) {
        lastEventIdRef.current = parsed;
      }
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) {
      clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  }, []);

  const attachListeners = useCallback((es: EventSource) => {
    for (const { type, wrapped } of listenersRef.current) {
      es.addEventListener(type, wrapped as EventListener);
    }
    if (heartbeatHandlerRef.current) {
      es.addEventListener('heartbeat', heartbeatHandlerRef.current as EventListener);
    }
  }, []);

  const detachListeners = useCallback((es: EventSource) => {
    for (const { type, wrapped } of listenersRef.current) {
      es.removeEventListener(type, wrapped as EventListener);
    }
    if (heartbeatHandlerRef.current) {
      es.removeEventListener('heartbeat', heartbeatHandlerRef.current as EventListener);
    }
  }, []);

  const connect = useCallback(() => {
    clearReconnectTimer();
    clearWatchdog();
    manualCloseRef.current = false;

    if (eventSourceRef.current) {
      detachListeners(eventSourceRef.current);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Pass last seen id as query param so the server can replay missed events.
    // This is the authoritative recovery channel — we don't rely on the browser
    // to retain Last-Event-ID across our manual close+reopen.
    const url = lastEventIdRef.current > 0
      ? `${Config.baseUrl}/api/sse/events?lastEventId=${lastEventIdRef.current}`
      : `${Config.baseUrl}/api/sse/events`;

    heartbeatHandlerRef.current = (e: MessageEvent) => noteEvent(e);

    const es = new EventSource(url);
    eventSourceRef.current = es;
    setConnectionState(ConnectionStatusEnum.connecting);

    es.onopen = () => {
      setIsConnected(true);
      setConnectionState(ConnectionStatusEnum.connected);
      setConnectionTransport('sse');
      setConnectionError('');
      lastEventAtRef.current = Date.now();

      attachListeners(es);

      if (wasConnectedRef.current) {
        onReconnect();
      }
      wasConnectedRef.current = true;

      watchdogTimerRef.current = setInterval(() => {
        if (!eventSourceRef.current) return;
        if (Date.now() - lastEventAtRef.current > STALE_THRESHOLD_MS) {
          console.warn('SSE: stale connection detected, forcing reconnect');
          setReplayCount(c => c + 1);
          connect();
        }
      }, WATCHDOG_INTERVAL_MS);
    };

    es.onerror = () => {
      setIsConnected(false);

      if (es.readyState === EventSource.CONNECTING) {
        setConnectionState(ConnectionStatusEnum.reconnected);
      } else if (es.readyState === EventSource.CLOSED) {
        setConnectionState(ConnectionStatusEnum.disconnected);
        setConnectionError('Connection closed — will retry');
        clearWatchdog();

        if (!manualCloseRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            console.log('SSE: attempting manual reconnect after CLOSED state');
            connect();
          }, RECONNECT_DELAY_MS);
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReconnect, clearReconnectTimer, clearWatchdog, attachListeners, detachListeners, noteEvent]);

  const disconnect = useCallback(() => {
    manualCloseRef.current = true;
    clearReconnectTimer();
    clearWatchdog();
    if (eventSourceRef.current) {
      detachListeners(eventSourceRef.current);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
      setConnectionState(ConnectionStatusEnum.disconnected);
    }
  }, [clearReconnectTimer, clearWatchdog, detachListeners]);

  const addListener = useCallback((type: string, handler: (e: MessageEvent) => void) => {
    const wrapped = (e: MessageEvent) => {
      noteEvent(e);
      handler(e);
    };
    listenersRef.current.push({ type, handler, wrapped });
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      eventSourceRef.current.addEventListener(type, wrapped as EventListener);
    }
  }, [noteEvent]);

  const removeAllListeners = useCallback(() => {
    if (eventSourceRef.current) {
      detachListeners(eventSourceRef.current);
    }
    listenersRef.current = [];
  }, [detachListeners]);

  useEffect(() => {
    return () => {
      clearReconnectTimer();
      clearWatchdog();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isConnected,
    connectionState,
    connectionTransport,
    connectionError,
    replayCount,
    connect,
    disconnect,
    addListener,
    removeAllListeners,
  };
}
